import { translateBackend } from '@assets/i18n/config';
import type { CustomField } from '@interfaces/customField';
import { Box } from '@ovComponents/1-primative/box/box';
import { Typography } from '@ovComponents/1-primative/typography/typography';
import { Button } from '@ovComponents/2-component/button/button';
import { Form } from '@ovComponents/2-component/form/form';
import {
  Table,
  TableBody,
  TableCell,
  TableHeadCell,
  TableRow,
} from '@ovComponents/2-component/table/table';
import { useCustomFieldRenderer } from '@ovComponents/3-pattern/customField/useCustomFieldRenderer';
import { kebabCase } from 'lodash-es';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { buildEnrichedCustomOptions, invalidFields } from '../utils';

export const ApprovalVisual = ({
  options,
  loading,
  continueFunc,
  activeCustomFields,
  derivedCustomFields,
  approvalData,
  updateApproval,
  ignoreCustomFieldTriggerRules,
}: {
  options: any;
  loading: boolean;
  continueFunc: any;
  activeCustomFields?: string[];
  derivedCustomFields?: CustomField[];
  approvalData?: any;
  updateApproval?: any;
  ignoreCustomFieldTriggerRules?: boolean;
}) => {
  const { t } = useTranslation(['workflowCompletions']);
  const [focused, setFocused] = useState<string[]>([]);
  const submit = () => {
    const fields = invalidFields(
      {
        ...options,
      },
      { ...approvalData },
    );
    const invalidCustomFieldKeys = validateAllCustomFields();
    fields.push(...invalidCustomFieldKeys);
    setFocused(fields);
    if (fields.length === 0) {
      continueFunc();
    }
  };

  const getMarkdown = (): string => {
    if (options?.filledTemplate) {
      return translateBackend(options?.filledTemplate);
    }
    return translateBackend(options?.template);
  };

  const [customOptionSelected, setCustomOptionSelected] = useState<any[]>([]);
  const { renderCustomFields, validateAllCustomFields } =
    useCustomFieldRenderer({
      customOptions: customOptionSelected,
      customFieldValues: approvalData?.customFields,
    });

  useEffect(() => {
    setCustomOptionSelected(
      buildEnrichedCustomOptions(
        options,
        activeCustomFields,
        derivedCustomFields,
      ),
    );
  }, [options, activeCustomFields, derivedCustomFields]);

  return (
    <Form onSubmit={submit}>
      <Typography variant='displayLarge' sx={{ mt: 1 }}>
        {translateBackend(options?.title)}
      </Typography>
      <Typography variant='bodyLarge' sx={{ mb: 3 }}>
        <ReactMarkdown linkTarget='_blank' remarkPlugins={[remarkGfm]}>
          {translateBackend(options?.subtitle)}
        </ReactMarkdown>
      </Typography>
      <Typography
        variant='bodyLarge'
        component={'div'}
        sx={{ mb: 3, table: { width: '100%' } }}
      >
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          linkTarget={'_blank'}
          components={{
            table: ({ node, ...props }) => (
              <Table {...props} sx={{ tableLayout: 'auto', width: '100%' }} />
            ),
            thead: ({ node, ...props }) => <TableBody {...props} />,
            th: ({ node, ...props }) => {
              const { align, ...tableProps } = props;
              return (
                <TableHeadCell
                  {...tableProps}
                  align={align === 'char' ? 'left' : align}
                  sx={{
                    minWidth: '100px',
                    paddingLeft: 0,
                  }}
                />
              );
            },
            td: ({ node, ...props }) => {
              const { align, ...tableProps } = props;
              return (
                <TableCell
                  {...tableProps}
                  align={align === 'char' ? 'left' : align}
                  sx={{
                    maxWidth: '100px',
                    minWidth: '100px',
                    paddingLeft: 0,
                  }}
                />
              );
            },
            tr: ({ node, ...props }) => <TableRow {...props} />,
            tbody: ({ node, ...props }) => <TableBody {...props} />,
          }}
        >
          {getMarkdown()}
        </ReactMarkdown>
      </Typography>
      {customOptionSelected &&
        customOptionSelected.length > 0 &&
        renderCustomFields({
          customData: approvalData,
          update: updateApproval,
          focused,
          setFocused,
          loading,
          ignoreCustomFieldTriggerRules,
        })}
      {(!options?.enableCallToActions ||
        !options?.callToActions ||
        options?.callToActions.length === 0) && (
        <Box display='flex' justifyContent='end'>
          <Button
            dataTestId={`approval-${kebabCase(options?.title?.en)}-continue-button`}
            label={t('workflowCompletions:continue')}
            disabled={loading}
            sx={{ mt: 3, textAlign: 'center' }}
            type='submit'
          />
        </Box>
      )}
      {options?.enableCallToActions &&
        options?.callToActions &&
        options?.callToActions.length > 0 && (
          <Box display='flex' justifyContent='end' gap={2}>
            {options.callToActions.map((action: any, index: number) => (
              <Button
                key={index}
                dataTestId={`approval-${kebabCase(options?.title?.en)}-action-${index}-button`}
                label={translateBackend(action.label)}
                variant={action.variant}
                color={action.color}
                disabled={loading}
                sx={{ mt: 3, textAlign: 'center' }}
                onClick={() => {
                  continueFunc(`branch-${index.toString()}`);
                }}
              />
            ))}
          </Box>
        )}
    </Form>
  );
};

export default ApprovalVisual;
